import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useAdmin } from './useAdmin';
import { useOrganizationContext } from '@/contexts/OrganizationContext';

export type UserRole = 'owner' | 'admin' | 'member';

export interface UserPermissions {
  role: UserRole | null;
  isAdmin: boolean;
  isOwner: boolean;
  isMember: boolean;
  canManageQueue: boolean;
  canViewAllChats: boolean;
  canAssignChats: boolean;
  isLoading: boolean;
}

export function useUserPermissions(): UserPermissions {
  const { user } = useAuth();
  const { currentOrg } = useOrganizationContext();
  const { isAdmin: isAdminMaster, loading: adminLoading } = useAdmin();

  const { data: role = null, isLoading } = useQuery<UserRole | null>({
    queryKey: ["user-role", currentOrg?.id, user?.id],
    enabled: !!user?.id && !!currentOrg?.id,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('organization_members')
        .select('role')
        .eq('org_id', currentOrg!.id)
        .eq('user_id', user!.id)
        .maybeSingle();
      if (error) throw error;
      return (data?.role as UserRole) ?? null;
    },
  });

  const effectiveRole = isAdminMaster ? 'owner' : role;
  const isAdmin = isAdminMaster || role === 'admin' || role === 'owner';
  const isOwner = isAdminMaster || role === 'owner';
  const isMember = role === 'member';

  return {
    role: effectiveRole,
    isAdmin,
    isOwner,
    isMember,
    canManageQueue: isAdmin,
    canViewAllChats: isAdmin,
    canAssignChats: isAdmin,
    isLoading: isLoading || adminLoading,
  };
}
